import objectHash from 'object-hash';
import { useContext, useEffect, useMemo } from 'react';

import { SliderProducts } from '@/components/Cms/SliderProducts';
import type { ProductFilterInput } from '@/graphql/saleor/api';
import { useAIRecommendations } from '@/hooks/useAIRecommendations';
import type { ProductCardFragmentWithVariantCta } from '@/hooks/useFetchProductsWithCta';
import { useFetchProductsWithCta } from '@/hooks/useFetchProductsWithCta';
import { CAROUSEL_FETCH_LIMIT } from '@/lib/const';
import { generateDataTestId } from '@/lib/tests';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';

import { CMSSection } from '../CMSSection';
import type { AutomaticCarouselProps } from './AutomaticCarousel';

export const AutomaticCarouselYouMayAlsoLike = ({
  currentProductSku,
  excludeSlug,
  filter,
  ...props
}: AutomaticCarouselProps) => {
  const draftMode = useContext(DraftModeContext);
  const pimSlugs = useMemo(() => filter?.slugs ?? [], [filter?.slugs]);
  const { mergedSlugs } = useAIRecommendations({
    currentProductSku,
    productType: 'Watch',
    pimSlugs,
  });

  const activeFilter = useMemo<ProductFilterInput | undefined>(
    () => (mergedSlugs.length > 0 ? { slugs: mergedSlugs } : filter),
    [mergedSlugs, filter],
  );

  const productCardsFetchLimit = useMemo(() => {
    const slugCount = Math.max(
      mergedSlugs.length,
      activeFilter?.slugs?.length ?? 0,
    );
    return Math.min(
      slugCount > 0 ? slugCount : CAROUSEL_FETCH_LIMIT,
      CAROUSEL_FETCH_LIMIT,
    );
  }, [mergedSlugs.length, activeFilter]);

  const { products, refetch } = useFetchProductsWithCta({
    filter: activeFilter,
    fetchLimit: productCardsFetchLimit,
    excludeSlug,
    orderedSlugs: mergedSlugs,
    skip: true,
  });

  const filterHash = objectHash(activeFilter || {});

  useEffect(() => {
    refetch();
  }, [filterHash, excludeSlug]);

  const cappedProducts = useMemo(
    () =>
      products.slice(
        0,
        CAROUSEL_FETCH_LIMIT,
      ) as ProductCardFragmentWithVariantCta[],
    [products],
  );

  if (!cappedProducts.length) {
    return null;
  }

  const cappedSlugs = cappedProducts.map((p) => p.slug);

  // Wrapper here, not in AutomaticCarousel — see AutomaticCarouselDefault.
  return (
    <CMSSection {...generateDataTestId('carouselAutomatic', 'section')}>
      <SliderProducts
        data-test-id='AutomaticCarouselSliderProducts'
        {...props}
        draftMode={draftMode}
        isAutomaticSlider={true}
        orderedSlugs={cappedSlugs}
        productData={cappedProducts}
      />
    </CMSSection>
  );
};
